import React, { createContext, useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
const USE_MOCK_AUTH = false;

import { mockCurrentUser } from "../data/mock";
import { devLog } from "../utils/devLog";
import {
  purgeLegacyAuthStorage,
  fetchSessionStatus,
  refreshSession,
  scheduleProactiveRefresh,
  fetchAuthEndpoint,
} from '../utils/authSession';
import { readRateLimitError } from '../utils/rateLimit';
import { persistRateLimitFromError } from '../utils/rateLimitStorage';
import {
  ensureCsrfToken,
  syncCsrfTokenFromCookie,
  rebootstrapCsrfToken,
} from '../utils/csrf';
import { redirectToLogin, notifySessionExpired } from '../utils/authRedirect';
import { useRateLimit } from './RateLimitContext';
import AuthBootstrapShell from '../components/auth/AuthBootstrapShell';

const PUBLIC_AUTH_ROUTES = new Set(['/', '/help', '/privacy', '/terms', '/auth/callback']);

/** Debounce window for React Strict Mode double-mount bootstrap. */
const AUTH_BOOTSTRAP_DEBOUNCE_MS = 750;

let authBootstrapInFlight = null;
let authBootstrapFinishedAt = 0;
let syncExpiryInFlight = null;

function isPublicAuthRoute(pathname) {
  if (PUBLIC_AUTH_ROUTES.has(pathname)) return true;
  // Client interview resume links — no login required
  if (typeof pathname === 'string' && pathname.startsWith('/r/')) return true;
  return false;
}

function isCsrfRefreshFailure(result) {
  return Boolean(result?.csrfRejected || result?.reason === 'csrf');
}

/**
 * Retry refresh once after a forced CSRF bootstrap (authSession already retries internally).
 * @returns {Promise<import('../utils/authSession').RefreshSessionResult>}
 */
async function attemptSessionRefresh() {
  let refreshed = await refreshSession();
  if (refreshed.ok || !isCsrfRefreshFailure(refreshed)) {
    return refreshed;
  }

  devLog.warn('AuthContext: refresh CSRF rejected, bootstrapping before retry');
  const token = await rebootstrapCsrfToken();
  if (!token) {
    return refreshed;
  }

  syncCsrfTokenFromCookie();
  return refreshSession();
}

/**
 * Only hard-redirect when /me confirms the session is no longer active.
 * @param {string} reason
 */
async function expireSessionUnlessStillActive(reason) {
  if (USE_MOCK_AUTH) {
    return;
  }

  try {
    const meResponse = await fetchAuthEndpoint('/api/auth/me', { method: 'GET' });
    if (meResponse.status === 200) {
      devLog.warn('AuthContext: /me still active; skipping session expire redirect', { reason });
      return;
    }
  } catch (error) {
    devLog.warn('AuthContext: /me check failed before expire', error?.message || 'unknown');
  }

  notifySessionExpired({ reason });
}

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const { isBlocked, isStatusReady } = useRateLimit();
  const [user, setUser] = useState(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const expiryCleanupRef = useRef(null);

  const handleSessionExpired = useCallback(async () => {
    devLog.warn('AuthContext: Session expired');
    await expireSessionUnlessStillActive('session_timer');
  }, []);

  const syncSessionExpiry = useCallback(async () => {
    if (syncExpiryInFlight) {
      return syncExpiryInFlight;
    }

    syncExpiryInFlight = (async () => {
      const status = await fetchSessionStatus();
      setSessionExpiresAt(status.expiresAt);

      if (expiryCleanupRef.current) {
        expiryCleanupRef.current();
        expiryCleanupRef.current = null;
      }

      if (status.canRefresh) {
        const refreshed = await attemptSessionRefresh();
        if (refreshed.ok) {
          syncExpiryInFlight = null;
          return syncSessionExpiry();
        }
        if (isCsrfRefreshFailure(refreshed)) {
          devLog.warn('AuthContext: refresh CSRF rejected; keeping active session');
          return;
        }
        await expireSessionUnlessStillActive('refresh_failed');
        return;
      }

      if (status.authenticated && status.expiresAt && status.refreshSupported) {
        expiryCleanupRef.current = scheduleProactiveRefresh(
          status.expiresAt,
          async () => {
            const result = await attemptSessionRefresh();
            if (result.ok) {
              await syncSessionExpiry();
            } else if (isCsrfRefreshFailure(result)) {
              devLog.warn('AuthContext: proactive refresh CSRF rejected; keeping active session');
            } else {
              await expireSessionUnlessStillActive('proactive_refresh_failed');
            }
          }
        );
      } else if (status.authenticated && status.expiresAt) {
        expiryCleanupRef.current = scheduleProactiveRefresh(
          status.expiresAt,
          handleSessionExpired,
          0
        );
      }
    })();

    try {
      await syncExpiryInFlight;
    } finally {
      syncExpiryInFlight = null;
    }
  }, [handleSessionExpired]);

  const fetchCurrentUser = useCallback(async () => {
    const now = Date.now();
    if (authBootstrapInFlight) {
      return authBootstrapInFlight;
    }
    if (authBootstrapFinishedAt && now - authBootstrapFinishedAt < AUTH_BOOTSTRAP_DEBOUNCE_MS) {
      devLog.log('AuthContext: skipping duplicate bootstrap');
      return;
    }

    const run = async () => {
      devLog.log("AuthContext: Fetching current user...");
      try {
        let response = await fetchAuthEndpoint('/api/auth/me', { method: 'GET' });

        if (response.status === 401) {
          const refreshed = await attemptSessionRefresh();
          if (refreshed.ok) {
            response = await fetchAuthEndpoint('/api/auth/me', { method: 'GET' });
          } else if (isCsrfRefreshFailure(refreshed)) {
            devLog.warn('AuthContext: bootstrap refresh CSRF rejected');
          }
        }

        if (response.status === 429) {
          const rateLimitError = await readRateLimitError(response);
          persistRateLimitFromError(rateLimitError);
          setIsLoading(false);
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch user: ${response.status}`);
        }

        const result = await response.json();
        if (result.success && result.data) {
          setUser(result.data);
          await syncSessionExpiry();
          setIsLoading(false);
        } else {
          throw new Error(result.error || 'Invalid user data');
        }
      } catch (error) {
        devLog.error("AuthContext: Error fetching user:", error.message);
        setUser(null);
        setSessionExpiresAt(null);
        setIsLoading(false);
      }
    };

    authBootstrapInFlight = run();
    try {
      await authBootstrapInFlight;
    } finally {
      authBootstrapFinishedAt = Date.now();
      authBootstrapInFlight = null;
    }
  }, [syncSessionExpiry]);

  useEffect(() => {
    if (!isStatusReady) {
      return undefined;
    }

    const checkAuth = async () => {
      if (USE_MOCK_AUTH) {
        setUser(mockCurrentUser);
        setIsLoading(false);
        return;
      }

      if (isBlocked) {
        setUser(null);
        setSessionExpiresAt(null);
        setIsLoading(false);
        return;
      }

      purgeLegacyAuthStorage();
      await ensureCsrfToken();
      syncCsrfTokenFromCookie();
      await fetchCurrentUser();
    };

    checkAuth();

    return () => {
      if (expiryCleanupRef.current) {
        expiryCleanupRef.current();
      }
    };
  }, [fetchCurrentUser, isBlocked, isStatusReady]);

  const login = useCallback(async () => {
    syncCsrfTokenFromCookie();
    await ensureCsrfToken();
    await fetchCurrentUser();
  }, [fetchCurrentUser]);

  const logout = useCallback(async () => {
    if (expiryCleanupRef.current) {
      expiryCleanupRef.current();
      expiryCleanupRef.current = null;
    }

    if (!USE_MOCK_AUTH) {
      try {
        await fetchAuthEndpoint('/api/auth/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
      } catch (error) {
        devLog.error("AuthContext: Logout request failed:", error.message);
      }
    }

    if (!USE_MOCK_AUTH) {
      redirectToLogin({ reason: 'logout' });
    } else {
      setUser(null);
      setSessionExpiresAt(null);
    }
  }, []);

  const authContextValue = useMemo(
    () => ({
      user,
      isLoading,
      login,
      logout,
      isAuthenticated: !!user,
      sessionExpiresAt,
    }),
    [user, isLoading, login, logout, sessionExpiresAt]
  );

  const showBootstrapLoader = isLoading && !isPublicAuthRoute(location.pathname);

  return (
    <AuthContext.Provider value={authContextValue}>
      {showBootstrapLoader ? <AuthBootstrapShell /> : children}
    </AuthContext.Provider>
  );
};
